import React, { useState } from "react";
import {
  Bolt,
  CheckCircle,
  ChevronRight,
  CloudUpload,
  DeleteOutline,
  EditOutlined,
  Error as ErrorIcon,
  ExpandLess,
  ExpandMore,
  HighlightAlt,
  RadioButtonUnchecked,
  SvgIconComponent,
} from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";

import { NavigationButton } from "ui";

import { deleteLayerFeatures } from "fond/project/redux";
import { useAppDispatch } from "fond/utils/hooks";
import { ConfirmModal } from "fond/widgets";

import AreaSelectSection from "./flyout/AreaSelectSection";
import BoundarySection from "./flyout/BoundarySection";
import { FlyoutFooterContext } from "./flyout/flyoutFooterContext";
import FlyoutHeader from "./flyout/FlyoutHeader";
import ReplaceDataConfirmation from "./flyout/ReplaceDataConfirmation";
import UploadSection from "./flyout/UploadSection";
import { useClearBoundary } from "./flyout/useBoundary";
import { formatList } from "./gating";
import { BASE_DATA_LAYERS, BaseDataLayerKey, BaseDataOption, REQUIRED_INPUT_KEYS } from "./layers";
import { BaseDataRowState } from "./rowState";
import { BaseDataGeneration } from "./useBaseDataGeneration";

/** Options offered as buttons; drawing is offered through the layer card's edit action. */
type ButtonOption = Exclude<BaseDataOption, "draw">;
type ExpandableOption = ButtonOption;

const OPTIONS: Record<ButtonOption, { label: string; icon: SvgIconComponent; description: string }> = {
  generate: { label: "Generate", icon: Bolt, description: "" },
  area: { label: "Area select", icon: HighlightAlt, description: "Use FOND data" },
  upload: { label: "Upload", icon: CloudUpload, description: "Use my data" },
};

const SECTIONS: Array<{ title: string; options: ButtonOption[] }> = [
  { title: "Generate", options: ["generate"] },
  { title: "Import data", options: ["area", "upload"] },
];

const OPTION_BUTTON_SX = { m: 0 };

interface BaseDataFlyoutProps {
  layerKey: BaseDataLayerKey;
  rowState: BaseDataRowState;
  generation: BaseDataGeneration;
  /**
   * Flag indicating if the project is read only
   */
  readOnly: boolean;
  /** Select another base data layer, or close the flyout with null. */
  onSelect(key: BaseDataLayerKey | null): void;
  onStartDraw(key: BaseDataLayerKey): void;
}

/** Map flyout for adding, generating and editing a single base data layer. */
const BaseDataFlyout: React.FC<BaseDataFlyoutProps> = ({ layerKey, rowState, generation, readOnly, onSelect, onStartDraw }) => {
  const dispatch = useAppDispatch();
  const clearBoundary = useClearBoundary();
  const [expanded, setExpanded] = useState<ExpandableOption | null>(null);
  const [isConfirmingClear, setIsConfirmingClear] = useState(false);
  const [footer, setFooter] = useState<HTMLElement | null>(null);
  const { name, shortName, icon: Icon, info, group, options, layerIds, units, layerNames } = BASE_DATA_LAYERS[layerKey];
  const { hasData, gating } = generation;
  const isLoading = rowState.status === "loading";
  const isLoaded = hasData[layerKey] && !isLoading;
  const { nextKey } = gating;
  const isBoundary = layerKey === "boundary";
  const isInput = group === "inputs";
  const clearLabel = isBoundary ? "Clear" : "Delete";
  const contents = layerNames ? formatList(layerNames) : units[1];

  const handleOnClear = () => {
    setIsConfirmingClear(false);
    if (isBoundary) {
      clearBoundary();
    } else {
      dispatch(deleteLayerFeatures(layerIds, layerKey));
    }
  };

  const handleOnSectionComplete = () => setExpanded(null);

  const renderOption = (option: ButtonOption) => {
    const { label, icon: OptionIcon, description } = OPTIONS[option];

    if (option === "generate") {
      const generationKey = layerKey === "ug" ? "ug" : "leadins";
      const gate = gating.generate[generationKey];
      const isConfirming = expanded === option;
      const confirmsReplace = hasData[layerKey];
      const generate = () => {
        setExpanded(null);
        generation.run([generationKey], { confirmed: true });
      };
      const ExpandIcon = isConfirming ? ExpandLess : ExpandMore;
      return (
        <Box key={option} display="flex" flexDirection="column" gap={0.75}>
          <NavigationButton
            icon={<OptionIcon />}
            title={label}
            subtitle={gate.reason ?? gating.generatedFrom[generationKey]}
            disabled={!gate.enabled}
            onClick={() => (confirmsReplace ? setExpanded(isConfirming ? null : option) : generate())}
            rightElement={confirmsReplace ? <ExpandIcon color="action" /> : <ChevronRight color="action" />}
            sx={{ ...OPTION_BUTTON_SX, ...(isConfirming && { borderColor: "primary.light" }) }}
          />
          {isConfirming && (
            <ReplaceDataConfirmation
              message={`Generating replaces the existing ${name.toLowerCase()}. Any edits you've made to it will be lost.`}
              onCancel={() => setExpanded(null)}
              onReplace={generate}
            />
          )}
        </Box>
      );
    }

    const isExpanded = expanded === option;
    return (
      <Box key={option} display="flex" flexDirection="column" gap={0.75}>
        <NavigationButton
          icon={<OptionIcon />}
          title={label}
          subtitle={description}
          disabled={readOnly}
          onClick={() => setExpanded(isExpanded ? null : option)}
          rightElement={isExpanded ? <ExpandLess color="action" /> : <ExpandMore color="action" />}
          sx={{ ...OPTION_BUTTON_SX, ...(isExpanded && { borderColor: "primary.light" }) }}
        />
        {isExpanded &&
          (option === "area" ? (
            <AreaSelectSection layerKey={layerKey} onComplete={handleOnSectionComplete} />
          ) : (
            <UploadSection layerKey={layerKey} onComplete={handleOnSectionComplete} />
          ))}
      </Box>
    );
  };

  return (
    <Box display="flex" flexDirection="column" height="100%" bgcolor="background.paper" data-testid="base-data-flyout">
      <FlyoutHeader icon={Icon} title={name} onClose={() => onSelect(null)} />

      <Box flexGrow={1} minHeight={0} overflow="auto" display="flex" flexDirection="column" gap={2.5} p={1.75}>
        <Alert severity="info" variant="compact">
          {info}
        </Alert>

        {(isLoaded || (!isBoundary && !isLoading)) && (
          <Box display="flex" alignItems="center" gap={1.25} border={1} borderColor="divider" borderRadius={1} px={1.5} py={0.5}>
            {!isLoaded && <RadioButtonUnchecked sx={{ color: (theme) => theme.palette.biarri.secondary.inactiveGrey }} />}
            {isLoaded && rowState.status === "partial" && <ErrorIcon sx={{ color: (theme) => theme.palette.warning.main }} />}
            {isLoaded && rowState.status !== "partial" && <CheckCircle sx={{ color: (theme) => theme.palette.success.main }} />}
            <Typography variant="body2" flexGrow={1} color={isLoaded ? undefined : "text.secondary"}>
              {isLoaded ? rowState.caption : `No ${shortName ?? name.toLowerCase()} yet`}
            </Typography>
            <Tooltip title="Edit on map">
              <span>
                <IconButton aria-label="Edit on map" color="primary" size="small" disabled={readOnly} onClick={() => onStartDraw(layerKey)}>
                  <EditOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isLoaded ? clearLabel : ""}>
              <span>
                <IconButton
                  aria-label={clearLabel}
                  color="error"
                  size="small"
                  disabled={readOnly || !isLoaded}
                  onClick={() => setIsConfirmingClear(true)}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}

        {isLoading && (
          <Box display="flex" alignItems="center" gap={1.25} border={1} borderColor="divider" borderRadius={1} px={1.5} py={1.25}>
            <CircularProgress size={18} color="warning" />
            <Typography variant="body2">Loading onto the map</Typography>
          </Box>
        )}

        {isBoundary ? (
          <FlyoutFooterContext.Provider value={footer}>
            <BoundarySection hasBoundary={hasData.boundary} onComplete={() => onSelect("addresses")} />
          </FlyoutFooterContext.Provider>
        ) : (
          SECTIONS.map(({ title, options: sectionOptions }) => {
            const available = sectionOptions.filter((option) => options.includes(option));
            if (available.length === 0) return null;
            return (
              <Box key={title} display="flex" flexDirection="column" gap={1.25}>
                <Typography variant="body2" fontWeight={500}>
                  {title}
                </Typography>
                {available.map(renderOption)}
              </Box>
            );
          })
        )}
      </Box>

      <Box
        ref={setFooter}
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        gap={1}
        px={1.75}
        py={1.25}
        borderTop={1}
        borderColor="divider"
        flexShrink={0}
        sx={{ "&:empty": { display: "none" } }}
      >
        {!isBoundary && (
          <>
            {isInput && (
              <Typography variant="caption" color="text.secondary" flexGrow={1}>
                {`${gating.requiredInputsAdded} of ${REQUIRED_INPUT_KEYS.length} required inputs added`}
              </Typography>
            )}
            {nextKey && nextKey !== layerKey ? (
              <Button variant="contained" size="small" endIcon={<ChevronRight />} onClick={() => onSelect(nextKey)}>
                {`Next: ${BASE_DATA_LAYERS[nextKey].name}`}
              </Button>
            ) : (
              <Button variant="outlined" size="small" onClick={() => onSelect(null)}>
                Done
              </Button>
            )}
          </>
        )}
      </Box>

      {isConfirmingClear && (
        <ConfirmModal
          open
          header={`${clearLabel} ${name.toLowerCase()}?`}
          confirmText={clearLabel}
          color="error"
          content={
            <Typography variant="body2">
              {isBoundary ? "This removes the boundary from this version." : `This removes all ${contents} from this version.`}
            </Typography>
          }
          onConfirm={handleOnClear}
          onCancel={() => setIsConfirmingClear(false)}
        />
      )}
    </Box>
  );
};

export default BaseDataFlyout;
